import React, { useEffect, useState } from "react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Pencil, KeyRound, Trash2, ShieldOff, ShieldCheck, History } from "lucide-react";

const ROLE_META = {
  admin:       { label: "Admin",       cls: "bg-amber-100 text-amber-800 border-amber-300" },
  manager:     { label: "Manager",     cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  researcher:  { label: "Researcher",  cls: "bg-sky-50 text-sky-700 border-sky-200" },
  integration: { label: "Integration", cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

function formatDate(iso, withTime = true) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return withTime ? d.toLocaleString() : d.toLocaleDateString();
}

// -----------------------------------------------------------------------------
// Create dialog
// -----------------------------------------------------------------------------
function CreateUserDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    email: "", password: "", name: "",
    role: "researcher", phone: "", department: "", title: "", notes: "",
  });

  useEffect(() => {
    if (open) setForm({
      email: "", password: "", name: "",
      role: "researcher", phone: "", department: "", title: "", notes: "",
    });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try {
      const { data } = await http.post("/auth/users", form);
      toast.success(`User ${data.email} created`);
      onCreated?.();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Add a team member</DialogTitle>
          <DialogDescription>Fill in complete details. Password must be at least 8 characters.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Full name *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cu-name" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Email *</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="cu-email" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Password *</Label>
              <Input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="cu-password" className="mt-1.5" placeholder="At least 8 characters" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Role *</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="cu-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="researcher">Researcher</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="integration">Integration</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="cu-phone" className="mt-1.5" placeholder="+91 …" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="cu-title" className="mt-1.5" placeholder="e.g. Senior Researcher" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Department</Label>
              <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="cu-department" className="mt-1.5" placeholder="e.g. Business Development" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cu-notes" className="mt-1.5" placeholder="Anything else the team should know" />
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="cu-cancel">Cancel</Button>
            <Button type="submit" data-testid="cu-submit" className="bg-amber-600 hover:bg-amber-700 text-white">Create user</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Edit dialog
// -----------------------------------------------------------------------------
function EditUserDialog({ open, onClose, initial, onUpdated, isSelf }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (open && initial) {
      setForm({
        name: initial.name || "",
        role: initial.role || "researcher",
        phone: initial.phone || "",
        department: initial.department || "",
        title: initial.title || "",
        notes: initial.notes || "",
        active: initial.active !== false,
      });
    }
  }, [open, initial]);
  if (!open || !initial) return null;

  const submit = async (e) => {
    e.preventDefault();
    try {
      await http.patch(`/auth/users/${initial.id}`, form);
      toast.success("User updated");
      onUpdated?.();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="edit-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Edit user — {initial.email}</DialogTitle>
          <DialogDescription>Update role, contact info, or active status. Password reset is separate.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="eu-name" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="eu-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="researcher">Researcher</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="integration">Integration</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="eu-phone" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="eu-title" className="mt-1.5" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Department</Label>
              <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="eu-department" className="mt-1.5" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="eu-notes" className="mt-1.5" />
            </div>
            <label className="md:col-span-2 flex items-center gap-2 text-sm mt-2">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} disabled={isSelf} data-testid="eu-active" />
              <span className="text-slate-700">Account active {isSelf && <span className="text-slate-400 text-xs">(cannot deactivate yourself)</span>}</span>
            </label>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="eu-cancel">Cancel</Button>
            <Button type="submit" data-testid="eu-submit" className="bg-amber-600 hover:bg-amber-700 text-white">Save changes</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Reset password dialog
// -----------------------------------------------------------------------------
function ResetPasswordDialog({ open, onClose, target, onDone }) {
  const [pwd, setPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  useEffect(() => { if (open) { setPwd(""); setConfirmPwd(""); } }, [open]);
  if (!open || !target) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pwd !== confirmPwd) { toast.error("Passwords do not match"); return; }
    try {
      await http.post(`/auth/users/${target.id}/reset-password`, { new_password: pwd });
      toast.success(`Password reset for ${target.email}. Share it out-of-band.`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[500px]" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-600" /> Reset password</DialogTitle>
          <DialogDescription>Set a new password for <span className="font-mono">{target.email}</span>. Share it with the user via a secure out-of-band channel (Signal, 1Password share, etc.).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">New password *</Label>
            <Input type="password" required value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="rp-password" className="mt-1.5" placeholder="At least 8 characters" />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600 tracking-wider">Confirm new password *</Label>
            <Input type="password" required value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} data-testid="rp-confirm" className="mt-1.5" />
          </div>
          <div className="text-xs text-slate-500 pt-1">
            The user's current sessions may remain valid until their access token expires (~8h). Ask them to sign out and back in.
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="rp-cancel">Cancel</Button>
            <Button type="submit" data-testid="rp-submit" className="bg-amber-600 hover:bg-amber-700 text-white">Reset password</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// User Management (main component)
// -----------------------------------------------------------------------------
export default function UserManagement() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = async () => {
    const [u, e] = await Promise.all([
      http.get("/auth/users"),
      http.get("/auth/user-events?limit=50").catch(() => ({ data: [] })),
    ]);
    setUsers(u.data);
    setEvents(e.data);
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    try {
      await http.patch(`/auth/users/${u.id}`, { active: !(u.active !== false) });
      toast.success(u.active !== false ? "User deactivated" : "User activated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    if (!deleting) return;
    try {
      const { data } = await http.delete(`/auth/users/${deleting.id}`);
      const revoked = data.tokens_revoked || 0;
      toast.success(`User deleted${revoked ? ` (${revoked} token${revoked === 1 ? "" : "s"} revoked)` : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setDeleting(null);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Users</h3>
          <p className="text-xs text-slate-500">Add team members, adjust their roles and details, reset passwords, and revoke access.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="btn-new-user"
                className="bg-amber-600 hover:bg-amber-700 text-white">
          <Plus className="w-4 h-4 mr-1.5" /> Add user
        </Button>
      </div>

      {/* Users table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-3 font-semibold">Name / Email</th>
                <th className="text-left px-4 py-3 font-semibold">Role</th>
                <th className="text-left px-4 py-3 font-semibold">Title / Dept</th>
                <th className="text-left px-4 py-3 font-semibold">Phone</th>
                <th className="text-left px-4 py-3 font-semibold">Created</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500" data-testid="users-empty">No users yet.</td></tr>
              )}
              {users.map((u) => {
                const active = u.active !== false;
                const roleMeta = ROLE_META[u.role] || ROLE_META.researcher;
                const isSelf = u.id === me.id;
                return (
                  <tr key={u.id} className="align-top" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{u.name}{isSelf && <span className="ml-2 text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">You</span>}</div>
                      <div className="text-xs text-slate-500 font-mono">{u.email}</div>
                      {u.notes && <div className="text-xs text-slate-500 mt-1 max-w-xs italic">{u.notes}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${roleMeta.cls}`}>
                        {roleMeta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">
                      {u.title && <div>{u.title}</div>}
                      {u.department && <div className="text-slate-500">{u.department}</div>}
                      {!u.title && !u.department && <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">{u.phone || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{formatDate(u.created_at, false)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                        active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-300"
                      }`} data-testid={`user-status-${u.id}`}>
                        {active ? "Active" : "Deactivated"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => setEditing(u)}
                                data-testid={`btn-edit-user-${u.id}`} title="Edit details">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setResetting(u)}
                                data-testid={`btn-reset-pwd-${u.id}`} title="Reset password">
                          <KeyRound className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => toggleActive(u)}
                                data-testid={`btn-toggle-user-${u.id}`}
                                disabled={isSelf}
                                title={active ? "Deactivate" : "Activate"}>
                          {active ? <ShieldOff className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDeleting(u)}
                                data-testid={`btn-delete-user-${u.id}`}
                                disabled={isSelf}
                                className="text-rose-700 hover:bg-rose-50 border-rose-200" title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Audit log */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
          <History className="w-4 h-4 text-slate-500" />
          <div className="font-heading font-semibold text-slate-900">Admin audit — user events</div>
          <span className="text-xs text-slate-500 ml-auto">{events.length} recent events</span>
        </div>
        {events.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-8" data-testid="user-events-empty">No user events yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50">
                <tr className="text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="text-left px-4 py-2 font-semibold">When</th>
                  <th className="text-left px-4 py-2 font-semibold">Event</th>
                  <th className="text-left px-4 py-2 font-semibold">Target</th>
                  <th className="text-left px-4 py-2 font-semibold">By</th>
                  <th className="text-left px-4 py-2 font-semibold">Changes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {events.map((e) => (
                  <tr key={e.id} className="text-sm" data-testid={`user-event-${e.id}`}>
                    <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">{formatDate(e.timestamp)}</td>
                    <td className="px-4 py-2"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-slate-50 text-slate-700 border-slate-200">{e.event_type.replace(/_/g, " ")}</span></td>
                    <td className="px-4 py-2 text-slate-700">{e.target_user_name || e.target_user_email}</td>
                    <td className="px-4 py-2 text-slate-700">{e.actor_name}</td>
                    <td className="px-4 py-2 text-xs text-slate-600 max-w-md truncate">
                      {(() => {
                        const keys = Object.keys(e.changes || {});
                        if (!keys.length) return "—";
                        return keys.map((k) => {
                          const v = e.changes[k];
                          if (v && typeof v === "object" && "before" in v && "after" in v) {
                            const fmt = (x) => x === null || x === undefined ? "∅" : typeof x === "string" ? `"${x}"` : String(x);
                            return `${k}: ${fmt(v.before)} → ${fmt(v.after)}`;
                          }
                          return `${k}: ${typeof v === "object" ? JSON.stringify(v).slice(0, 40) : v}`;
                        }).join(" · ");
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dialogs */}
      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />
      <EditUserDialog open={!!editing} initial={editing} onClose={() => setEditing(null)} onUpdated={load} isSelf={editing?.id === me.id} />
      <ResetPasswordDialog open={!!resetting} target={resetting} onClose={() => setResetting(null)} onDone={load} />

      {/* Delete confirm */}
      {deleting && (
        <AlertDialog open onOpenChange={(o) => !o && setDeleting(null)}>
          <AlertDialogContent data-testid="delete-user-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading">Delete this user?</AlertDialogTitle>
              <AlertDialogDescription>
                <span className="font-mono">{deleting.email}</span> will be permanently removed.{" "}
                {deleting.role === "integration" && (
                  <>Any API tokens they own will be <strong>revoked immediately</strong>, causing n8n workflows using them to fail.</>
                )}
                {" "}This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-user-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="delete-user-confirm" onClick={doDelete}
                className="bg-rose-600 hover:bg-rose-700">Delete permanently</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
